import { Asset, MetadataFieldType, MetadataValue, UUID } from '@/api/generatedApi';
import { Ellipsis } from '@/components/Ellipsis';
import { RoundedSquareIconButton } from '@/components/RoundedSquareIconButton';
import { usePopover } from '@/hooks/usePopover';
import { Edit } from '@mui/icons-material';
import { Box, Tooltip } from '@mui/material';
import { AssetTableCellLabel, AssetTableCellValue, AssetTableRow } from '../AssetDetails/AssetTable';
import { MetadataEdit } from './MetadataEdit';
import { StyledPopover } from '@/components/AssetsPage/PopoverList';

export interface MetadataFieldDisplay {
  assetId?: UUID;
  fieldName: string;
  fieldType: MetadataFieldType;
  fieldValue: MetadataValue;
}

export const MetadataTableRow = ({
  asset,
  field: { fieldName, fieldType, fieldValue },
  onEditSuccess,
}: {
  asset?: Asset;
  field: MetadataFieldDisplay;
  onEditSuccess: (editedMetadata: Record<string, MetadataValue>) => void;
}) => {
  const {
    open: handleEditClick,
    close: handleEditClose,
    isOpen: editOpen,
    anchorElement: editAnchorElement,
  } = usePopover();

  const handleEditSuccess = (editedMetadata: Record<string, MetadataValue>) => {
    onEditSuccess(editedMetadata);
    handleEditClose();
  };

  return (
    <>
      <AssetTableRow
        data-field-name={fieldName}
        tabIndex={0}
        sx={{
          '&:focus': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: '-1px',
          },
        }}
      >
        <AssetTableCellLabel sx={{ paddingRight: 1 }}>
          <Ellipsis text={fieldName} />
        </AssetTableCellLabel>
        <AssetTableCellValue>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              width: '100%',
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              {fieldType === MetadataFieldType.Url ? (
                <a
                  href={fieldValue.toString()}
                  target='_blank'
                  rel='noopener noreferrer'
                  style={{ color: 'inherit', textDecoration: 'underline' }}
                >
                  <Ellipsis text={fieldValue.toString()} />
                </a>
              ) : (
                <Ellipsis text={fieldValue.toString()} />
              )}
            </Box>
            <Box
              className='action-buttons'
              sx={{
                display: 'flex',
                gap: '4px',
              }}
            >
              <RoundedSquareIconButton
                sx={{ backgroundColor: 'background.paper' }}
                size='small'
                onClick={handleEditClick}
              >
                <Tooltip title='Edit'>
                  <Edit sx={{ fontSize: '12px' }} />
                </Tooltip>
              </RoundedSquareIconButton>
            </Box>
          </Box>
        </AssetTableCellValue>
      </AssetTableRow>

      <StyledPopover
        open={editOpen}
        anchorEl={editAnchorElement}
        onClose={handleEditClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              marginLeft: '-18px',
              borderRadius: '12px',
            },
          },
        }}
      >
        {asset && (
          <MetadataEdit
            asset={asset}
            fieldName={fieldName}
            fieldType={fieldType}
            currentValue={fieldValue}
            onSuccess={() => handleEditSuccess({ [fieldName]: fieldValue })}
            onCancel={handleEditClose}
          />
        )}
      </StyledPopover>
    </>
  );
};
